/**
 * <cos-leanpay-calc> — interactive financing calculator.
 *
 * Reads config from a `data-settings` JSON blob on the element:
 *   { min, max, step, default, annualRate, months: [n, ...] }
 *
 * Internal IDs are scoped by section id (data-section-id attribute) to
 * allow multiple instances on one page without id collisions.
 *
 * Formula: standard annuity (PMT)
 *   r = annualRate / 12
 *   pmt = P * r / (1 - (1 + r)^-n)
 *
 * Updates on every slider `input` and month pill click:
 *   - slider track fill via CSS var --p
 *   - amount display
 *   - active month pill class
 *   - rata lunara display
 *   - total display
 *
 * No bare-specifier imports. Self-contained ES module.
 */
class CosLeanpayCalc extends HTMLElement {
  connectedCallback() {
    const raw = this.getAttribute('data-settings');
    if (!raw) return;

    let cfg;
    try {
      cfg = JSON.parse(raw);
    } catch (e) {
      console.error('[cos-leanpay-calc] Invalid data-settings JSON', e);
      return;
    }

    this._cfg = {
      min: Number(cfg.min) || 250,
      max: Number(cfg.max) || 25000,
      step: Number(cfg.step) || 50,
      defaultAmount: Number(cfg.default) || 5000,
      annualRate: (function (v) { var n = parseFloat(v); return n > 0 && n < 2 ? n : 0.1899; })(cfg.annualRate),
      months: Array.isArray(cfg.months) ? cfg.months.map(Number) : [6, 12, 24, 60],
    };

    this._activeMonths = this._cfg.months[this._cfg.months.length - 1];
    this._fmt = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 });

    this._slider = this.querySelector('[data-calc-slider]');
    this._amtDisplay = this.querySelector('[data-calc-amount]');
    this._rataDisplay = this.querySelector('[data-calc-rata]');
    this._totalDisplay = this.querySelector('[data-calc-total]');
    this._monthsContainer = this.querySelector('[data-calc-months]');

    if (!this._slider || !this._amtDisplay || !this._rataDisplay ||
        !this._totalDisplay || !this._monthsContainer) {
      console.warn('[cos-leanpay-calc] Required child elements not found.');
      return;
    }

    this._slider.min = this._cfg.min;
    this._slider.max = this._cfg.max;
    this._slider.step = this._cfg.step;
    this._slider.value = this._cfg.defaultAmount;

    // Static scale labels — format once (no-JS fallback shows raw numbers)
    const scaleMin = this.querySelector('[data-calc-scale-min]');
    const scaleMax = this.querySelector('[data-calc-scale-max]');
    if (scaleMin) scaleMin.textContent = this._fmt.format(this._cfg.min);
    if (scaleMax) scaleMax.textContent = this._fmt.format(this._cfg.max);

    this._onSlider = this._onSlider.bind(this);
    this._onMonthClick = this._onMonthClick.bind(this);

    this._slider.addEventListener('input', this._onSlider);
    this._monthsContainer.addEventListener('click', this._onMonthClick);

    this._paint();
    this._calc();
  }

  disconnectedCallback() {
    if (this._slider) this._slider.removeEventListener('input', this._onSlider);
    if (this._monthsContainer) this._monthsContainer.removeEventListener('click', this._onMonthClick);
  }

  _onSlider() {
    this._paint();
    this._calc();
  }

  _onMonthClick(e) {
    const pill = e.target.closest('[data-months-n]');
    if (!pill) return;
    const n = Number(pill.dataset.monthsN);
    if (!n) return;
    this._activeMonths = n;
    this._monthsContainer.querySelectorAll('[data-months-n]').forEach(function(el) {
      el.classList.toggle('lp-calc__month--active', Number(el.dataset.monthsN) === n);
    });
    this._calc();
  }

  _paint() {
    const slider = this._slider;
    const pct = ((slider.value - slider.min) / (slider.max - slider.min)) * 100;
    slider.style.setProperty('--p', pct + '%');
  }

  _calc() {
    const P = Number(this._slider.value);
    const r = this._cfg.annualRate / 12;
    const n = this._activeMonths;
    const pmt = r ? P * r / (1 - Math.pow(1 + r, -n)) : P / n;

    this._amtDisplay.textContent = this._fmt.format(P);
    this._rataDisplay.textContent = this._fmt.format(pmt);
    this._totalDisplay.textContent = this._fmt.format(pmt * n) + ' lei';
  }
}

if (!customElements.get('cos-leanpay-calc')) {
  customElements.define('cos-leanpay-calc', CosLeanpayCalc);
}
